import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import EnterMobile from "@/components/Verification/VerifyMobile";
import VerifyOTP from "@/components/Verification/VerifyOTP";

import { IMobileVerify, IOtpVerify } from "@/models/onboarding";
import { IRoleType } from "@/models/role";
import { IAddPatient } from "@/models/doctor";

import {
  mobileVerifyOrSendOTPApi,
  OTPVerifyApi,
} from "@/utils/apis/onboarding/OnBoardingApis";
import { storage } from "@/utils/Storage";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { INotifyEnum, notify } from "@/utils/toaster";
import { getLocation, isClinic } from "@/utils";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { useRedirectAfterLogin } from "@/utils/hooks/useRedirectAfterLogin";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setAddPatient } from "@/store/slices/doctor/topNavigation";
import { setRoleAndPermission } from "@/store/slices/permission";
import {
  fetchInAppNotification,
  setIsLocationAccessOpen,
  setIsShowEditProfileModal,
} from "@/store/slices/config";
import {
  setFCMDeviceToken,
  videoAppointmentDetails,
} from "@/store/slices/videoAppointment";

import firebaseService from "@/firebaseConfig";
import { usePatientProfile } from "@/utils/hooks/usePatientProfile";

const MobileOTPVerify: React.FC = () => {
  const [isMobileVerifyShow, setIsMobileVerifyShow] = useState<boolean | null>(
    true
  );
  const [phone, setPhone] = useState({
    number: "",
    countryCode: "",
    mobileLength: "",
    otpId: "",
  });
  const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const { mobileCheck } = useAppSelector(
    (state) => state.topNavigation.addPatient
  );
  const { fcmDeviceToken } = useAppSelector(videoAppointmentDetails);

  const dispatch = useAppDispatch();
  const { isMobile } = useIsMobile();
  const isTablet = useIsTablet();

  const handleRedirectAfterLogin = useRedirectAfterLogin();
  const { fetchPatientProfileDetail, isProfileLoading } = usePatientProfile();

  const handleRedirection = async (role: string) => {
    dispatch(fetchInAppNotification({ page: 1 }));
    if (isClinic()) {
      navigate("/clinic-patient");
    } else {
      switch (role) {
        case IRoleType.DOCTOR:
        case IRoleType.JR_DOCTOR:
          navigate("/schedule");
          break;
        case IRoleType.PHARMACY:
        case IRoleType.DIAGNOSTIC_CENTER:
          navigate("/orders");
          break;
        default:
          const location = getLocation();
          if (!location) {
            dispatch(setIsLocationAccessOpen(true));
          } else if (role !== IRoleType.GUEST) {
            const profileDetail = await fetchPatientProfileDetail();
            setIsBtnLoading(isProfileLoading);
            if (
              !profileDetail?.name ||
              !profileDetail?.age ||
              !profileDetail?.gender
            ) {
              dispatch(setIsShowEditProfileModal(true));
            } else {
              handleRedirectAfterLogin();
            }
          }
          setIsMobileVerifyShow(null);
          break;
      }
    }
  };

  const mobileSubmitHandler = async (values: IMobileVerify, code: string) => {
    setIsBtnLoading(true);
    const deviceToken =
      fcmDeviceToken?.length === 0
        ? await firebaseService.generateTokenFCM()
        : fcmDeviceToken;
    dispatch(setFCMDeviceToken(deviceToken));
    try {
      const mobileVerifyRes = await mobileVerifyOrSendOTPApi({
        country_code: code,
        phone_number: values.mobileNo,
      });
      if ("result" in mobileVerifyRes) {
        setPhone({
          number: values.mobileNo,
          countryCode: code,
          mobileLength: values.mobileNo,
          otpId: mobileVerifyRes?.result?.OtpId,
        });

        setIsMobileVerifyShow(!isMobileVerifyShow);
      } else {
        notify(INotifyEnum.ERROR, mobileVerifyRes?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsBtnLoading(false);
    }
  };

  const mobileCheckOpenClosed = (value: boolean) => {
    dispatch(
      setAddPatient({
        field: IAddPatient.MOBILE_CHECK,
        value,
      })
    );
  };

  const handleOTPSubmit = async (values: IOtpVerify) => {
    const deviceToken =
      fcmDeviceToken?.length === 0
        ? await firebaseService.generateTokenFCM()
        : fcmDeviceToken;
    dispatch(setFCMDeviceToken(deviceToken));
    setIsBtnLoading(true);
    try {
      const otpVerifyRes = await OTPVerifyApi({
        otp_code: values.otp,
        token: phone?.otpId,
      });
      if ("result" in otpVerifyRes) {
        const {
          role,
          name,
          user_id,
          token,
          clinic_id,
          doctor_id,
          doctor_name,
          permissions,
          phone_number,
          profile_photo,
          doctorBasicInfo,
          country_code,
          video_consultaion_fee,
        } = otpVerifyRes?.result;
        dispatch(setRoleAndPermission(permissions));

        const sessionDate = {
          token,
          user_id,
          roleType: role,
          name,
          phone_number,
          profile_photo,
          country_code,
          ...(clinic_id &&
            doctor_id && {
              clinic_id,
              doctor_id,
              doctor_name,
            }),
          ...(doctorBasicInfo && {
            doctorId: doctorBasicInfo.user_id,
            doctorName: doctorBasicInfo.name,
          }),
          ...((role === IRoleType.DOCTOR || role === IRoleType.JR_DOCTOR) && {
            consultationFee: video_consultaion_fee,
          }),
        };

        // set session data
        Object.entries(sessionDate).forEach(([key, value]) =>
          storage.setSessionToken(key, value)
        );

        handleRedirection(role);
        mobileCheckOpenClosed(false);
      } else {
        if (otpVerifyRes.statusCode === IAPIStatusCode.BLOCKED) {
          mobileCheckOpenClosed(false);
          if (isMobile || isTablet) {
            navigate("/home");
          }
        }
        notify(
          INotifyEnum.ERROR,
          otpVerifyRes.message || "OTP verification failed"
        );
      }
    } catch (error) {
      console.log(error);
      mobileCheckOpenClosed(false);
    } finally {
      setIsBtnLoading(false);
    }
  };

  const resendOTP = async () => {
    try {
      const mobileVerifyRes = await mobileVerifyOrSendOTPApi({
        country_code: phone?.countryCode,
        device_id: "string",
        device_token: "string",
        phone_number: phone?.number,
      });
      if ("result" in mobileVerifyRes) {
        setPhone({
          ...phone,
          otpId: mobileVerifyRes.result.OtpId,
        });
      } else {
        notify(INotifyEnum.ERROR, mobileVerifyRes?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const navigateToMobileVerify = () =>
    setIsMobileVerifyShow(!isMobileVerifyShow);

  const mobileCheckCloseHandler = () => {
    !isMobile
      ? (setIsMobileVerifyShow(null), mobileCheckOpenClosed(false))
      : {};
  };

  useEffect(() => {
    if (mobileCheck) setIsMobileVerifyShow(mobileCheck);
  }, [mobileCheck]);

  return (
    <>
      {isMobileVerifyShow ? (
        <EnterMobile
          isBtnLoading={isBtnLoading}
          isOpen={isMobileVerifyShow}
          closeHandler={mobileCheckCloseHandler}
          heading="loginUsingPhoneText"
          mobileSubmitHandler={mobileSubmitHandler}
          btnLabel="getOTP"
          mobileNo={phone?.number}
        />
      ) : (
        isMobileVerifyShow !== null && (
          <VerifyOTP
            isBtnLoading={isBtnLoading}
            handleOTPSubmit={handleOTPSubmit}
            mobileNo={phone?.number}
            navigateToMobileVerify={navigateToMobileVerify}
            resendOTP={resendOTP}
          />
        )
      )}
    </>
  );
};

export default MobileOTPVerify;
